import { computed, ref } from 'vue'
import * as Sentry from '@sentry/nuxt'
import { invoke } from '~/lib/tauriInvoke'
import {
  MESSAGE_SEARCH_PAGE_SIZE,
  toHits,
  type MessageSearchHas,
  type MessageSearchHit,
  type SearchMessagesResponse,
} from '~/stores/messageSearch'

// One member's messages in a group, newest first — the «Сообщения» / «Ссылки» lists in the user
// profile. It is the group message search (`search_messages`, the same endpoint as the chat
// header's search) with an «От:» filter (plus «Есть:», e.g. `link`) and no query, paged with the
// server's page token. Kept apart from
// `useMessageSearchStore` on purpose: opening a profile mustn't replace whatever the user has
// searched for in the chat.
export function useUserMessages() {
  const groupId = ref<string | null>(null)
  const userId = ref<string | null>(null)
  const has = ref<MessageSearchHas[]>([])
  const hits = ref<MessageSearchHit[]>([])
  const total = ref(0)
  const nextPageToken = ref('')
  const isLoading = ref(false)
  const isLoadingMore = ref(false)
  const error = ref<string | null>(null)
  const hasMore = computed(() => !!nextPageToken.value)

  // Bumped by every new load/reset, so a page that answers after the list moved on is dropped.
  let seq = 0

  function request(pageToken: string) {
    return invoke<SearchMessagesResponse>('search_messages', {
      payload: {
        group_id: groupId.value,
        query: '',
        channel_ids: [],
        author_ids: [userId.value],
        has: has.value,
        before: null,
        after: null,
        sort: 'newest',
        page_size: MESSAGE_SEARCH_PAGE_SIZE,
        page_token: pageToken || null,
      },
    })
  }

  function reset() {
    seq++
    hits.value = []
    total.value = 0
    nextPageToken.value = ''
    isLoading.value = false
    isLoadingMore.value = false
    error.value = null
  }

  /** First page for this member of this group; `withHas` narrows it (e.g. `['link']`). */
  async function load(group: string, user: string, withHas: MessageSearchHas[] = []) {
    reset()
    groupId.value = group
    userId.value = user
    has.value = withHas
    const current = seq
    isLoading.value = true
    try {
      const res = await request('')
      if (current !== seq) return
      hits.value = toHits(res)
      total.value = res.total
      nextPageToken.value = res.next_page_token
    } catch (e: any) {
      if (current !== seq) return
      error.value = e?.message || String(e)
      Sentry.captureException(e, { extra: { action: 'search_user_messages', groupId: group, userId: user } })
    } finally {
      if (current === seq) isLoading.value = false
    }
  }

  /** Next page (infinite scroll). */
  async function loadMore() {
    if (!nextPageToken.value || isLoading.value || isLoadingMore.value) return
    const current = seq
    isLoadingMore.value = true
    try {
      const res = await request(nextPageToken.value)
      if (current !== seq) return
      const seen = new Set(hits.value.map((h) => h.message.id))
      hits.value = [...hits.value, ...toHits(res).filter((h) => !seen.has(h.message.id))]
      total.value = Math.max(res.total, hits.value.length)
      nextPageToken.value = res.next_page_token
    } catch (e: any) {
      if (current !== seq) return
      error.value = e?.message || String(e)
    } finally {
      if (current === seq) isLoadingMore.value = false
    }
  }

  return { hits, total, isLoading, isLoadingMore, error, hasMore, load, loadMore, reset }
}
